import { date as dfDate } from '@orgazone/date-format';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { api, hhmmss, userStatusLabel, type ApiUser } from '../api';
import { Icon, type IconName } from '../components/icons';
import { Modal } from '../components/Modal';
import { Badge, Btn, Card, DataGate, Dot, PageHead } from '../components/ui';
import { useApp } from '../state';
import { useResource } from '../useResource';

// Utilisateurs (§24.9quinquies) — ⚠️ PAS de gestion d'accès ici : les comptes/rôles/mots de passe fédérés
// vivent au SUPER-ADMIN + IdP (grants → scopes dans le token). Cet écran se réduit à : (1) le compte de
// SECOURS local (bris de glace §h) — seul élément éditable (rotation du mot de passe) ; (2) une vue en
// LECTURE des accès fédérés (traçabilité) ; (3) le journal d'authentification. Rien n'est simulé (§23.0).

type Toast = (m: string, t?: 'ok' | 'warn' | 'err' | 'info') => void;

// Libellés FR des actions d'audit auth (défaut = l'action brute).
const AUDIT_LABEL: Record<string, string> = {
  'login.success': 'Connexion réussie', 'login.failure': 'Échec de connexion', 'login.locked': 'Compte verrouillé',
  'login.2fa_required': '2FA demandée', '2fa.verify.success': '2FA validée', '2fa.verify.failure': '2FA échouée',
  logout: 'Déconnexion', 'logout.all': 'Déconnexion (partout)', 'session.revoke': 'Session révoquée',
  'password.change': 'Mot de passe changé', 'password.reset.request': 'Réinitialisation demandée',
  'password.reset.complete': 'Réinitialisation effectuée', '2fa.enable': '2FA activée', '2fa.disable': '2FA désactivée',
};
// Rendu partagé (@orgazone/date-format) : « 04/08/2026 14:32:07 ».
const fmtDate = (iso: string): string => `${dfDate(iso)} ${hhmmss(iso)}`;
const roleLabel = (r: string): string => (r === 'admin' ? 'Admin' : r === 'viewer' ? 'Lecture' : r);

export function Utilisateurs() {
  const { showToast, guard } = useApp();
  const [rotate, setRotate] = useState<ApiUser | null>(null);

  // Les deux lectures partent ENSEMBLE : indépendantes l'une de l'autre, et l'écran n'est pas lisible à
  // moitié (les accès sans leur journal ne racontent rien). Une seule qui échoue fait donc échouer l'écran.
  // ⚠️ AUCUN `isEmpty` : l'écran garde sa structure (bandeau + sections) même sans aucun compte, chaque
  // section portant son propre vide — d'où le retrait de la garde `empty`, qui ne pouvait pas s'afficher.
  const { state, data, reload, error } = useResource(async (signal) => {
    const [users, audit] = await Promise.all([api.users(signal), api.audit(signal)]);
    return { users, audit };
  });

  const audit = data?.audit ?? [];
  // ⚠️ Mémorisés sur `data`, jamais sur une liste dérivée : celle-ci serait neuve à chaque rendu et le
  // `useMemo` ne mémoriserait plus rien.
  const local = useMemo(() => data?.users.filter((u) => u.local) ?? [], [data]);
  const federated = useMemo(() => data?.users.filter((u) => !u.local) ?? [], [data]);

  // ⚠️ TITRE DE PAGE AJOUTÉ (relevé de la maquette) : l'écran ouvrait sur son bandeau d'avertissement,
  // donc sur ce qu'il NE fait pas avant de dire ce qu'il fait.
  const pageHead = (
    <PageHead title="Utilisateurs" sub="Compte de secours local, accès fédérés en lecture, journal d'authentification" />
  );

  return (
    <DataGate state={state} onRetry={reload} error={error} head={pageHead}>
      {/* Bandeau : la gestion des accès est ailleurs (super-admin) */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--blue-bg)', color: 'var(--blue-fg)', borderRadius: 'var(--r-sm)', padding: '11px 14px', marginBottom: 16, fontSize: 'var(--t-body)', lineHeight: 1.45 }}>
        <span style={{ marginTop: 1 }}><Icon name="users" size={15} /></span>
        <span>La <strong>gestion des accès</strong> (comptes, rôles, mots de passe fédérés) se fait dans l'<strong>Orgazone Console</strong> (super-admin) — l'IdP émet les rôles dans le jeton. Ici : le <strong>compte de secours</strong> local et une <strong>vue en lecture</strong> des accès fédérés.</span>
      </div>

      {/* Compte(s) de secours (bris de glace) — SEUL élément éditable */}
      <SectionTitle icon="lock" title="Compte de secours (bris de glace)" />
      {local.length === 0 && <Card style={{ padding: 14, marginBottom: 18, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun compte de secours local — sème-le avec <code>node dist/seed.js</code> (§h).</Card>}
      <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
        {local.map((u) => (
          <Card key={u.id} style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="lock" size={16} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}>{u.email}</div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 1 }}>{roleLabel(u.role)} · dernière connexion {u.lastLoginAt ? fmtDate(u.lastLoginAt) : 'jamais'}</div>
              </div>
              <Badge kind={u.status === 'active' ? 'ok' : 'off'}>{userStatusLabel(u.status)}</Badge>
              <Btn size="sm" icon="refresh" variant="ghost" onClick={() => { if (guard()) setRotate(u); }}>Réinitialiser le mot de passe</Btn>
            </div>
          </Card>
        ))}
      </div>

      {/* Accès fédérés (lecture seule) */}
      <SectionTitle icon="users" title={`Accès fédérés (${federated.length})`} note="lecture seule — gérés au super-admin" />
      <Card style={{ overflow: 'hidden', marginBottom: 18 }}>
        {federated.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun accès fédéré — les comptes apparaissent après leur 1ʳᵉ connexion SSO.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 560 }}>
              <thead><tr style={{ background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
                {['Utilisateur', 'Rôle', 'Dernière connexion', 'État'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {federated.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--ink-100)' }}>
                    <td style={{ padding: '9px 14px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: 'var(--ink-800)' }}>{u.email}</td>
                    <td style={{ padding: '9px 14px', fontSize: 'var(--t-dense)', color: u.role === 'admin' ? 'var(--teal-700)' : 'var(--ink-600)', fontWeight: 600 }}>{roleLabel(u.role)}</td>
                    <td style={{ padding: '9px 14px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{u.lastLoginAt ? fmtDate(u.lastLoginAt) : '—'}</td>
                    <td style={{ padding: '9px 14px' }}><Badge kind={u.status === 'active' ? 'ok' : 'off'}>{userStatusLabel(u.status)}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Journal d'authentification */}
      <SectionTitle icon="history" title="Journal d'authentification" note="lecture seule" />
      <Card style={{ overflow: 'hidden' }}>
        {audit.length === 0 && <div style={{ padding: '14px 16px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun événement.</div>}
        {audit.map((e, i) => (
          <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderBottom: i < audit.length - 1 ? '1px solid var(--ink-100)' : 'none', fontSize: 'var(--t-dense)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{fmtDate(e.at)}</span>
            <Dot kind={e.action.includes('failure') || e.action.includes('locked') ? 'err' : 'ok'} />
            <span style={{ color: 'var(--ink-800)', minWidth: 150 }}>{AUDIT_LABEL[e.action] ?? e.action}</span>
            <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-500)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.email ?? '—'}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{e.ip ?? ''}</span>
          </div>
        ))}
      </Card>

      {rotate && <RotateModal user={rotate} onClose={() => setRotate(null)} onDone={async () => { setRotate(null); await reload(); }} showToast={showToast} />}
    </DataGate>
  );
}

function SectionTitle({ icon, title, note }: { icon: IconName; title: string; note?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 2px 10px' }}>
      <span style={{ color: 'var(--ink-500)' }}><Icon name={icon} size={15} /></span>
      <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-800)' }}>{title}</span>
      {note && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>· {note}</span>}
    </div>
  );
}

// Modale — rotation du mot de passe du compte de secours (bris de glace). Mot de passe fort (≥ 12) + confirmation.
function RotateModal({ user, onClose, onDone, showToast }: { user: ApiUser; onClose: () => void; onDone: () => void; showToast: Toast }) {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const { guard } = useApp(); // défense en profondeur : le handler revérifie la capacité d'écriture
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const pwdRef = useRef<HTMLInputElement>(null);
  // ⚠️ Le champ prend le focus APRÈS la modale (l'effet d'un parent passe après celui de l'enfant) : un `autoFocus`
  //    serait pris pour le déclencheur, et le focus ne reviendrait plus au bouton à la fermeture.
  useEffect(() => { pwdRef.current?.focus(); }, []);
  const tooShort = pwd.length > 0 && pwd.length < 12;
  const mismatch = confirm.length > 0 && confirm !== pwd;
  const ok = pwd.length >= 12 && confirm === pwd;
  const inp = { width: '100%', height: 38, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)' } as const;
  const lbl = { fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', display: 'block', marginBottom: 5 } as const;

  const save = async () => {
    if (!guard() || !ok) return;
    setBusy(true);
    try {
      await api.rotatePassword(user.id, pwd);
      showToast('Mot de passe du compte de secours mis à jour', 'ok');
      onDone();
    } catch (e) {
      showToast('Échec : ' + (e as Error).message, 'err');
      setBusy(false);
    }
  };

  return (
    <Modal icon="lock" title="Réinitialiser le compte de secours" sub={user.email} onClose={onClose}
      footer={<><Btn variant="ghost" mutating={false} onClick={onClose}>Annuler</Btn><Btn disabled={!ok || busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Btn></>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ background: 'var(--amber-bg)', color: 'var(--ink-700)', borderRadius: 'var(--r-sm)', padding: '9px 12px', fontSize: 'var(--t-note)', lineHeight: 1.45 }}>
          ⚠️ Le compte de secours ne sert qu'au dépannage (IdP à terre). Choisis un mot de passe FORT et coffre-le.
        </div>
        <div>
          <label htmlFor={`${fieldId}-0`} style={lbl}>Nouveau mot de passe (≥ 12 caractères)</label>
          <input id={`${fieldId}-0`} ref={pwdRef} type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} style={{ ...inp, borderColor: tooShort ? 'var(--rose)' : 'var(--ink-200)' }} autoComplete="new-password" />
          {tooShort && <div style={{ fontSize: 'var(--t-sub)', color: 'var(--rose-fg)', marginTop: 4 }}>Au moins 12 caractères.</div>}
        </div>
        <div>
          <label htmlFor={`${fieldId}-1`} style={lbl}>Confirmer</label>
          <input id={`${fieldId}-1`} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={{ ...inp, borderColor: mismatch ? 'var(--rose)' : 'var(--ink-200)' }} autoComplete="new-password" onKeyDown={(e) => e.key === 'Enter' && void save()} />
          {mismatch && <div style={{ fontSize: 'var(--t-sub)', color: 'var(--rose-fg)', marginTop: 4 }}>Ne correspond pas.</div>}
        </div>
      </div>
    </Modal>
  );
}
